import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTip, ResponsiveContainer,
  Cell, ReferenceLine, LineChart, Line, Legend, CartesianGrid
} from 'recharts';
import {
  TrendingUp, FileText, RefreshCw, Loader2, ChevronRight,
  BarChart2, Target, Users, Zap, Download, Gauge
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MD_TABLE_COMPONENTS } from '../../Common/MarkdownContent';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../../../api';
import toast from 'react-hot-toast';
import {
  SimResults, Manifest, ConstraintConfig, AIScenarioConfig,
  ScenarioLevers, ScenarioResult, Message
} from '../datasim.types';
import { weeksToDate, formatCompletionDate } from '../datasim.utils';

const PRESET_SCENARIOS: { name: string; levers: ScenarioLevers }[] = [
  { name: '+2 Team Members', levers: { add_team_members: 2 } },
  { name: 'Reduce Scope 20%', levers: { reduce_scope_pct: 20 } },
  { name: 'AI + Scope Cut + Headcount', levers: { add_team_members: 3, reduce_scope_pct: 15, reduce_variability_pct: 25 } },
];

type Tab = 'distribution' | 'sensitivity' | 'teams' | 'workload' | 'ai-impact' | 'scenarios' | 'action-plan';

interface Props {
  results: SimResults;
  manifest: Manifest | null;
  constraintConfig: ConstraintConfig;
  aiConfig: AIScenarioConfig | null;
  unit: string;
  projectStartDate?: string;
  messages?: Message[];
  onReset: () => void;
}

export default function DataSimResults({ results, manifest, constraintConfig, aiConfig, unit, projectStartDate, messages, onReset }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('distribution');
  const [actionPlan, setActionPlan] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionTone, setActionTone] = useState<'program_manager' | 'executive' | 'technical'>('program_manager');
  const [exportLoading, setExportLoading] = useState(false);
  const [scenarioRows, setScenarioRows] = useState<ScenarioResult[]>([]);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [customLevers, setCustomLevers] = useState<ScenarioLevers>({});
  const resultsRef = useRef<HTMLDivElement>(null);
  const statsHeaderRef = useRef<HTMLDivElement>(null);
  const tabPanelRef = useRef<HTMLDivElement>(null);

  const pm = results.primary_metric;
  const hasAI = !!results.ai_impact;
  const hasTeams = (results.team_results?.length ?? 0) > 1;

  const unitLower = (unit || '').toLowerCase();
  const isDurationUnit = ['week', 'weeks', 'month', 'months', 'day', 'days'].includes(unitLower);
  const valueToWeeks = (val: number): number => {
    if (unitLower.startsWith('month')) return val * 4.345;
    if (unitLower.startsWith('day')) return val / 7;
    return val;
  };
  const showDates = isDurationUnit && !!projectStartDate;
  const dateFor = (val: number): string => {
    if (!showDates) return '';
    return formatCompletionDate(weeksToDate(projectStartDate, valueToWeeks(val)));
  };
  const hasReadiness = (results.readiness_sensitivity?.length ?? 0) > 0;

  const activeConstraints = [
    constraintConfig.planning_fallacy.enabled && 'Planning Fallacy',
    constraintConfig.scope_creep.enabled && 'Scope Creep',
    constraintConfig.parkinson.enabled && "Parkinson's",
    constraintConfig.student_syndrome.enabled && 'Student Syndrome',
    constraintConfig.external_deps.enabled && 'External Deps',
  ].filter(Boolean) as string[];

  async function generateActionPlan() {
    if (!manifest) return;
    setActionLoading(true);
    setActionPlan('');
    try {
      const res = await api.post('/api/datasim/action-plan', {
        manifest, results, tone: actionTone, hasAIScenario: hasAI,
      });
      setActionPlan(res.data.action_plan);
    } catch {
      toast.error('Failed to generate action plan');
    } finally {
      setActionLoading(false);
    }
  }

  async function exportPDF() {
    setExportLoading(true);
    const originalTab = activeTab;
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const contentW = pageW - margin * 2;
      let pageNum = 0;

      const addFooter = () => {
        pageNum++;
        pdf.setFontSize(8);
        pdf.setTextColor(160, 160, 160);
        pdf.text(`${manifest?.project_name || 'DataSim'} — Monte Carlo Risk Report`, margin, pageH - 6);
        pdf.text(`Page ${pageNum}`, pageW - margin, pageH - 6, { align: 'right' });
        pdf.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageW / 2, pageH - 6, { align: 'center' });
        // Top line accent
        pdf.setDrawColor(239, 68, 68);
        pdf.setLineWidth(0.8);
        pdf.line(0, 0, pageW, 0);
      };

      const appendCanvas = (canvas: HTMLCanvasElement, startY: number = margin) => {
        const imgW = contentW;
        const imgH = (canvas.height * imgW) / canvas.width;
        const usableH = pageH - startY - 14; // leave footer space

        if (imgH <= usableH) {
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, startY, imgW, imgH);
        } else {
          let y = 0;
          let firstSlice = true;
          while (y < imgH) {
            if (!firstSlice) { pdf.addPage(); addFooter(); }
            firstSlice = false;
            const sliceStartY = firstSlice ? startY : margin;
            const sliceUsableH = pageH - sliceStartY - 14;
            const srcY = (y / imgH) * canvas.height;
            const sliceH = Math.min((sliceUsableH / imgH) * canvas.height, canvas.height - srcY);
            const slice = document.createElement('canvas');
            slice.width = canvas.width;
            slice.height = sliceH;
            slice.getContext('2d')!.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
            pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, imgW, (sliceH * imgW) / canvas.width);
            y += sliceUsableH;
          }
        }
      };

      // ── Cover Page ──────────────────────────────────────────────────
      // Red accent bar
      pdf.setFillColor(239, 68, 68);
      pdf.rect(0, 0, pageW, 3, 'F');

      // TSG branding line
      pdf.setFontSize(10);
      pdf.setTextColor(160, 160, 160);
      pdf.text('THE STEMMLER GROUP', margin, 20);
      pdf.setFontSize(8);
      pdf.text('JARVIS INTELLIGENCE PLATFORM', margin, 26);

      // Divider
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(0.3);
      pdf.line(margin, 32, pageW - margin, 32);

      // Project title
      pdf.setFontSize(22);
      pdf.setTextColor(30, 30, 30);
      pdf.text(manifest?.project_name || 'DataSim Report', margin, 50);

      // Subtitle
      pdf.setFontSize(11);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Monte Carlo Risk Simulation Report', margin, 58);

      // Date
      pdf.setFontSize(9);
      pdf.setTextColor(140, 140, 140);
      pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), margin, 66);

      // Summary stats box
      const boxY = 78;
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, boxY, contentW, 50, 3, 3, 'F');
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, boxY, contentW, 50, 3, 3, 'S');

      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.text('SIMULATION SUMMARY', margin + 8, boxY + 10);

      const statCols = [
        { label: 'P10 (Best)', val: `${pm.p10} ${unit}`, color: [34, 197, 94] },
        { label: 'P50 (Likely)', val: `${pm.p50} ${unit}`, color: [59, 130, 246] },
        { label: 'P90 (Worst)', val: `${pm.p90} ${unit}`, color: [239, 68, 68] },
        { label: 'Iterations', val: results.iterations.toLocaleString(), color: [107, 114, 128] },
        { label: 'Variables', val: `${results.variable_count}`, color: [107, 114, 128] },
      ];

      const colW = contentW / statCols.length;
      statCols.forEach((s, i) => {
        const cx = margin + i * colW + colW / 2;
        pdf.setFontSize(8);
        pdf.setTextColor(140, 140, 140);
        pdf.text(s.label, cx, boxY + 24, { align: 'center' });
        pdf.setFontSize(16);
        pdf.setTextColor(s.color[0], s.color[1], s.color[2]);
        pdf.text(s.val, cx, boxY + 36, { align: 'center' });
      });

      // Constraints and AI badges
      let badgeY = boxY + 58;
      pdf.setFontSize(9);
      pdf.setTextColor(107, 114, 128);
      pdf.text('Active Constraints:', margin, badgeY);
      let bx = margin + 38;
      activeConstraints.forEach(c => {
        pdf.setFontSize(8);
        const tw = pdf.getTextWidth(c) + 6;
        pdf.setFillColor(254, 242, 242);
        pdf.roundedRect(bx, badgeY - 4, tw, 7, 1.5, 1.5, 'F');
        pdf.setTextColor(220, 38, 38);
        pdf.text(c, bx + 3, badgeY + 1);
        bx += tw + 3;
      });

      if (hasAI) {
        badgeY += 12;
        pdf.setFontSize(9);
        pdf.setTextColor(107, 114, 128);
        pdf.text('AI Impact:', margin, badgeY);
        pdf.setFontSize(8);
        pdf.setTextColor(124, 58, 237);
        pdf.text(`P90 improves from ${pm.p90} to ${results.ai_impact?.primary_metric.p90 ?? '-'} ${unit}`, margin + 22, badgeY);
      }

      // Cover footer
      pdf.setFontSize(8);
      pdf.setTextColor(180, 180, 180);
      pdf.text('Confidential — For internal use only', margin, pageH - 10);
      pdf.text('Powered by Jarvis DataSim Risk Analyzer', pageW - margin, pageH - 10, { align: 'right' });

      // ── Text section helper ────────────────────────────────────────
      const startTextSection = (title: string, desc: string): number => {
        pdf.addPage();
        addFooter();
        pdf.setFillColor(239, 68, 68);
        pdf.rect(0, 0, pageW, 3, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text(`${manifest?.project_name || 'DataSim'} — Risk Report`, margin, 18);
        pdf.setDrawColor(239, 68, 68);
        pdf.setLineWidth(0.6);
        pdf.line(margin, 26, margin + 30, 26);
        pdf.setFontSize(18);
        pdf.setTextColor(30, 30, 30);
        pdf.text(title, margin, 36);
        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text(desc, margin, 44, { maxWidth: contentW });
        return 54;
      };

      const LINE_H = 4.6;
      const renderMarkdownLine = (raw: string, x: number, startY: number, maxW: number): number => {
        let y = startY;
        const text = raw.replace(/\s+$/, '');

        if (/^---+\s*$/.test(text)) {
          pdf.setDrawColor(229, 231, 235);
          pdf.setLineWidth(0.2);
          pdf.line(x, y - 1, x + maxW, y - 1);
          return y + 3;
        }
        if (text.trim() === '') return y + LINE_H * 0.6;

        const isBullet = /^[-•]\s+/.test(text);
        const bodyText = isBullet ? text.replace(/^[-•]\s+/, '') : text;
        const indent = isBullet ? 4 : 0;
        const usableW = maxW - indent;

        // Split into bold / normal segments by **...**
        const segments: { text: string; bold: boolean }[] = [];
        const re = /\*\*(.+?)\*\*/g;
        let last = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(bodyText)) !== null) {
          if (m.index > last) segments.push({ text: bodyText.slice(last, m.index), bold: false });
          segments.push({ text: m[1], bold: true });
          last = m.index + m[0].length;
        }
        if (last < bodyText.length) segments.push({ text: bodyText.slice(last), bold: false });
        if (segments.length === 0) segments.push({ text: bodyText, bold: false });

        // Word-wrap across segments, carrying bold state
        let cursorX = x + indent;
        let lineStartedBullet = isBullet;
        const flushNewLine = () => { y += LINE_H; cursorX = x + indent; };
        for (const seg of segments) {
          pdf.setFont('helvetica', seg.bold ? 'bold' : 'normal');
          const words = seg.text.split(/(\s+)/);
          for (const w of words) {
            if (w === '') continue;
            const wWidth = pdf.getTextWidth(w);
            if (cursorX + wWidth > x + indent + usableW && cursorX > x + indent) {
              flushNewLine();
              if (/^\s+$/.test(w)) continue;
            }
            if (lineStartedBullet && cursorX === x + indent) {
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(156, 163, 175);
              pdf.text('•', x, y);
              pdf.setFont('helvetica', seg.bold ? 'bold' : 'normal');
              pdf.setTextColor(55, 65, 81);
              lineStartedBullet = false;
            }
            pdf.text(w, cursorX, y);
            cursorX += wWidth;
          }
        }
        pdf.setFont('helvetica', 'normal');
        return y + LINE_H;
      };

      const interviewMessages = (messages || []).filter(m => m && typeof m.content === 'string' && m.content.trim());
      if (interviewMessages.length > 0) {
        let y = startTextSection(
          'Interview Summary',
          'The analyst interview that drove this simulation — the inputs and assumptions behind the numbers.'
        );
        for (const msg of interviewMessages) {
          const isAnalyst = msg.role === 'assistant';
          const speaker = isAnalyst ? 'DataSim Analyst' : 'Project Lead';

          if (y > pageH - 28) y = startTextSection('Interview Summary (continued)', '');

          // Speaker label chip
          pdf.setFillColor(isAnalyst ? 254 : 239, isAnalyst ? 242 : 246, isAnalyst ? 242 : 255);
          pdf.roundedRect(margin, y - 4, pdf.getTextWidth(speaker.toUpperCase()) + 6, 6, 1, 1, 'F');
          pdf.setFontSize(7.5);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(isAnalyst ? 220 : 59, isAnalyst ? 38 : 130, isAnalyst ? 38 : 246);
          pdf.text(speaker.toUpperCase(), margin + 3, y);
          pdf.setFont('helvetica', 'normal');
          y += 7;

          pdf.setFontSize(9);
          pdf.setTextColor(55, 65, 81);
          const lines = msg.content.trim().split('\n');
          for (const line of lines) {
            if (y > pageH - 16) y = startTextSection('Interview Summary (continued)', '');
            y = renderMarkdownLine(line, margin + 2, y, contentW - 4);
          }
          y += 6;
        }
      }

      // ── Scenario Comparison section ────────────────────────────────
      if (scenarioRows.length > 0) {
        let y = startTextSection(
          'Scenario Comparison',
          'How the timeline shifts under different decisions — each scenario re-ran the full Monte Carlo simulation.'
        );
        const cols = showDates
          ? [{ h: 'Scenario', w: 56, align: 'left' as const }, { h: `P50 (${unit})`, w: 24, align: 'right' as const }, { h: 'P50 Date', w: 30, align: 'right' as const }, { h: `P90 (${unit})`, w: 24, align: 'right' as const }, { h: 'vs Baseline', w: 0, align: 'right' as const }]
          : [{ h: 'Scenario', w: 68, align: 'left' as const }, { h: `P50 (${unit})`, w: 32, align: 'right' as const }, { h: `P90 (${unit})`, w: 32, align: 'right' as const }, { h: 'vs Baseline', w: 0, align: 'right' as const }];
        cols[cols.length - 1].w = contentW - cols.reduce((s, c) => s + c.w, 0);
        const rowH = 9;
        const cellPad = 3;

        const drawTableHeader = (startY: number): number => {
          pdf.setFillColor(243, 244, 246);
          pdf.rect(margin, startY, contentW, rowH, 'F');
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(75, 85, 99);
          let hx = margin;
          cols.forEach(c => {
            const tx = c.align === 'right' ? hx + c.w - cellPad : hx + cellPad;
            pdf.text(c.h, tx, startY + 6, { align: c.align });
            hx += c.w;
          });
          pdf.setFont('helvetica', 'normal');
          return startY + rowH;
        };

        y = drawTableHeader(y);

        scenarioRows.forEach((row, idx) => {
          if (y + rowH > pageH - 16) {
            y = startTextSection('Scenario Comparison (continued)', '');
            y = drawTableHeader(y);
          }
          if (row.is_baseline) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, y, contentW, rowH, 'F');
          } else if (idx % 2 === 1) {
            pdf.setFillColor(252, 252, 253);
            pdf.rect(margin, y, contentW, rowH, 'F');
          }
          const delta = row.delta_p50 ?? 0;
          const deltaStr = row.is_baseline ? '—'
            : `${delta < 0 ? '−' : '+'}${Math.abs(delta)} ${unit}${delta < 0 ? ' faster' : delta > 0 ? ' slower' : ''}`;
          const cells = showDates
            ? [row.name, String(row.p50), dateFor(row.p50), String(row.p90), deltaStr]
            : [row.name, String(row.p50), String(row.p90), deltaStr];
          let cx = margin;
          cells.forEach((cell, ci) => {
            const isLast = ci === cells.length - 1;
            pdf.setFontSize(9);
            pdf.setFont('helvetica', row.is_baseline || ci === 0 ? 'bold' : 'normal');
            if (isLast && !row.is_baseline) {
              pdf.setTextColor(delta < 0 ? 5 : delta > 0 ? 220 : 107, delta < 0 ? 150 : delta > 0 ? 38 : 114, delta < 0 ? 105 : delta > 0 ? 38 : 128);
            } else {
              pdf.setTextColor(row.is_baseline ? 31 : 55, row.is_baseline ? 41 : 65, row.is_baseline ? 55 : 81);
            }
            const tx = cols[ci].align === 'right' ? cx + cols[ci].w - cellPad : cx + cellPad;
            pdf.text(String(cell), tx, y + 6, { align: cols[ci].align, maxWidth: cols[ci].w - cellPad * 2 });
            cx += cols[ci].w;
          });
          pdf.setFont('helvetica', 'normal');
          pdf.setDrawColor(235, 237, 240);
          pdf.setLineWidth(0.2);
          pdf.line(margin, y + rowH, margin + contentW, y + rowH);
          y += rowH;
        });
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.3);
        pdf.rect(margin, y - rowH * scenarioRows.length - rowH, contentW, rowH * (scenarioRows.length + 1), 'S');
      }

      // ── Section pages ──────────────────────────────────────────────
      const tabsToExport = TABS.filter(t => t.show !== false);
      const sectionNames: Record<string, { title: string; desc: string }> = {
        distribution: { title: 'Outcome Distribution', desc: 'Probability distribution of total project duration across 10,000 Monte Carlo iterations.' },
        sensitivity: { title: 'Sensitivity Analysis', desc: 'Spearman rank correlation identifying which variables drive the most outcome uncertainty.' },
        teams: { title: 'Team Breakdown', desc: 'Per-team completion windows and critical path probability analysis.' },
        'ai-impact': { title: 'AI Impact Analysis', desc: 'Comparison of base scenario vs AI-accelerated scenario with readiness sensitivity.' },
        'action-plan': { title: 'Action Plan', desc: 'AI-generated risk mitigation recommendations based on simulation results.' },
      };

      for (const tab of tabsToExport) {
        if (tab.id === 'action-plan' && !actionPlan) continue;
        if (tab.id === 'scenarios') continue; // rendered as a structured text section above

        // Section header page
        pdf.addPage();
        addFooter();
        pdf.setFillColor(239, 68, 68);
        pdf.rect(0, 0, pageW, 3, 'F');

        const section = sectionNames[tab.id] || { title: tab.label, desc: '' };
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text(`${manifest?.project_name || 'DataSim'} — Risk Report`, margin, 18);

        pdf.setDrawColor(239, 68, 68);
        pdf.setLineWidth(0.6);
        pdf.line(margin, 26, margin + 30, 26);

        pdf.setFontSize(18);
        pdf.setTextColor(30, 30, 30);
        pdf.text(section.title, margin, 36);

        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text(section.desc, margin, 44, { maxWidth: contentW });

        // Capture tab content
        setActiveTab(tab.id);
        await new Promise(r => setTimeout(r, 500));
        if (tabPanelRef.current) {
          const canvas = await html2canvas(tabPanelRef.current, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            height: tabPanelRef.current.scrollHeight,
            windowHeight: tabPanelRef.current.scrollHeight,
          });
          appendCanvas(canvas, 50);
        }
      }

      setActiveTab(originalTab);
      pdf.save(`datasim-${manifest?.project_name || 'report'}-${Date.now()}.pdf`);
      toast.success('PDF report exported!');
    } catch {
      toast.error('PDF export failed');
      setActiveTab(originalTab);
    } finally {
      setExportLoading(false);
    }
  }

  async function runScenarioBatch(definitions: { name: string; levers: ScenarioLevers }[]) {
    if (!manifest || definitions.length === 0) return;
    setScenarioLoading(true);
    try {
      const res = await api.post('/api/datasim/scenarios-batch', {
        manifest,
        iterations: 10000,
        scenarios: definitions,
      });
      const data = res.data || {};
      const rows: ScenarioResult[] = [
        data.baseline,
        ...(Array.isArray(data.scenarios) ? data.scenarios : []),
      ].filter(Boolean);
      setScenarioRows(rows);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Scenario comparison failed');
    } finally {
      setScenarioLoading(false);
    }
  }

  const hasCustomLevers = Object.values(customLevers).some(v => typeof v === 'number' && v > 0);

  const workload = results.workload_check;
  const hasWorkload = !!workload?.applicable && (workload.per_team?.length ?? 0) > 0;

  const TABS: { id: Tab; label: string; icon: React.ElementType; show?: boolean }[] = [
    { id: 'distribution', label: 'Distribution', icon: BarChart2 },
    { id: 'sensitivity',  label: 'Sensitivity',  icon: TrendingUp },
    { id: 'teams',        label: 'Teams',         icon: Users, show: hasTeams },
    { id: 'workload',     label: 'Workload',      icon: Gauge, show: hasWorkload },
    { id: 'ai-impact',    label: 'AI Impact',     icon: Zap, show: hasAI },
    { id: 'scenarios',    label: 'Scenarios',     icon: Target },
    { id: 'action-plan',  label: 'Action Plan',   icon: FileText },
  ];

  return (
    <div className="space-y-5" ref={resultsRef}>

      {/* ── Stats header ──────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" ref={statsHeaderRef}>
        <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-red-50 to-rose-50 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-red-600" />
            <span className="text-sm font-bold text-gray-800">
              {manifest?.project_name || 'Simulation'} — Results
            </span>
            {hasAI && (
              <span className="text-xs bg-violet-100 text-violet-700 border border-violet-200 px-2 py-0.5 rounded-full font-medium">
                + AI Impact
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {results.iterations.toLocaleString()} iterations · {results.variable_count} variables
            </span>
            {activeConstraints.map(c => (
              <span key={c} className="text-xs bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded font-medium">{c}</span>
            ))}
            <button
              onClick={exportPDF}
              disabled={exportLoading}
              className="flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {exportLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              Export PDF
            </button>
          </div>
        </div>

        {/* P-value cards */}
        <div className="p-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {[
            { label: 'P10', sub: 'Best Case',    val: pm.p10, aiVal: results.ai_impact?.primary_metric.p10, color: '#22c55e', bg: 'bg-green-50' },
            { label: 'P25', sub: 'Optimistic',   val: pm.p25, aiVal: results.ai_impact?.primary_metric.p25, color: '#86efac', bg: 'bg-green-50/40' },
            { label: 'P50', sub: 'Most Likely',  val: pm.p50, aiVal: results.ai_impact?.primary_metric.p50, color: '#3b82f6', bg: 'bg-blue-50' },
            { label: 'P75', sub: 'Conservative', val: pm.p75, aiVal: results.ai_impact?.primary_metric.p75, color: '#fb923c', bg: 'bg-orange-50/40' },
            { label: 'P90', sub: 'Worst Case',   val: pm.p90, aiVal: results.ai_impact?.primary_metric.p90, color: '#ef4444', bg: 'bg-red-50' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
              <p className="text-xs font-bold text-gray-500 mb-0.5">{s.label}</p>
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.val}</p>
              {hasAI && s.aiVal != null && (
                <p className="text-xs font-semibold text-violet-600 mt-0.5">
                  {s.aiVal} <span className="text-violet-400 font-normal">w/AI</span>
                </p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">{unit}</p>
              {showDates && (
                <p className="text-xs font-medium text-gray-600 mt-1 pt-1 border-t border-gray-200/70">
                  {dateFor(s.val)}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-2 border-t border-gray-100 bg-gray-50 flex justify-between text-xs text-gray-500">
          <span>Mean: <strong className="text-gray-700">{pm.mean}</strong></span>
          <span>Std Dev: <strong className="text-gray-700">{pm.std_dev}</strong></span>
          <span>Spread: <strong className="text-gray-700">{(pm.p90 - pm.p10).toFixed(1)} {unit}</strong></span>
          {hasAI && results.ai_impact && (
            <span className="text-violet-600">
              AI P90 improvement: <strong>{(pm.p90 - results.ai_impact.primary_metric.p90).toFixed(1)} {unit}</strong>
            </span>
          )}
        </div>
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" ref={tabPanelRef}>
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.filter(t => t.show !== false).map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600 bg-red-50/40'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'ai-impact' && <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* ── Distribution ─────────────────────────────────────────── */}
        {activeTab === 'distribution' && (
          <div className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Outcome Distribution</p>
            <p className="text-xs text-gray-400 mb-4">
              Each bar = count of simulations landing in that range. Blue dashed = P50. Green = P10. Red = P90.
              {hasAI && ' Violet overlay = AI Impact distribution.'}
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={results.histogram} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                <XAxis dataKey="bin" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => String(v)} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <RechartsTip
                  formatter={(val: any, _: any, props: any) => {
                    const bin = props.payload.bin;
                    const dateStr = dateFor(bin);
                    return [`${val.toLocaleString()} runs`, dateStr ? `${bin} ${unit} · ${dateStr}` : `${bin} ${unit}`];
                  }}
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                />
                <ReferenceLine x={pm.p50} stroke="#3b82f6" strokeDasharray="4 4" strokeWidth={1.5} />
                <ReferenceLine x={pm.p10} stroke="#22c55e" strokeDasharray="3 3" strokeWidth={1} />
                <ReferenceLine x={pm.p90} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1} />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {results.histogram.map((entry, i) => (
                    <Cell key={i} fill={
                      entry.bin <= pm.p10 ? '#22c55e' :
                      entry.bin >= pm.p90 ? '#ef4444' :
                      entry.bin <= pm.p50 ? '#60a5fa' : '#93c5fd'
                    } fillOpacity={0.75} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-800 mb-1">Interpretation</p>
              <p className="text-sm text-blue-700">
                <strong>90% probability</strong> this completes within <strong>{pm.p90} {unit}</strong>
                {showDates && <> — by <strong>{dateFor(pm.p90)}</strong></>}.{' '}
                <strong>50% probability</strong> within <strong>{pm.p50} {unit}</strong>
                {showDates && <> — by <strong>{dateFor(pm.p50)}</strong></>}.{' '}
                Optimistic (P10): <strong>{pm.p10} {unit}</strong>
                {showDates && <> — by <strong>{dateFor(pm.p10)}</strong></>}.
              </p>
            </div>
          </div>
        )}

        {/* ── Sensitivity ───────────────────────────────────────────── */}
        {activeTab === 'sensitivity' && (
          <div className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Risk Drivers — Sensitivity</p>
            <p className="text-xs text-gray-400 mb-4">
              Spearman rank correlation between each variable and the program outcome. Higher = more impact.
            </p>
            <div className="space-y-3">
              {results.sensitivity_rankings.map((r, i) => (
                <div key={r.variable_id} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-5 text-right font-mono">{i + 1}</span>
                  <div className="w-44 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{r.variable_name}</p>
                    {r.unit && <p className="text-xs text-gray-400">{r.unit}</p>}
                  </div>
                  <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                    <div className="h-full rounded transition-all duration-700"
                      style={{ width: `${Math.abs(r.spearman_r) * 100}%`,
                        backgroundColor: i === 0 ? '#ef4444' : i === 1 ? '#f97316' : i <= 3 ? '#f59e0b' : '#6b7280',
                        opacity: 0.8 }}
                    />
                  </div>
                  <span className="text-xs font-mono text-gray-500 w-12 text-right">r={r.spearman_r.toFixed(2)}</span>
                </div>
              ))}
            </div>
            {results.sensitivity_rankings.length > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800">Key insight</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  <strong>{results.sensitivity_rankings[0]?.variable_name}</strong> is the top driver
                  (r = {results.sensitivity_rankings[0]?.spearman_r.toFixed(2)}). Reducing uncertainty here
                  has the greatest impact on program confidence.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Team Breakdown ────────────────────────────────────────── */}
        {activeTab === 'teams' && hasTeams && (
          <div className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Team Completion Windows</p>
            <p className="text-xs text-gray-400 mb-4">
              Per-team P50/P90 completion and probability of being on the critical path.
            </p>
            <div className="space-y-4">
              {(results.team_results || []).map(t => {
                const onCritPath = Math.round(t.probability_on_critical_path * 100);
                const isCritical = onCritPath > 50;
                return (
                  <div key={t.team_id} className={`border rounded-xl p-4 ${isCritical ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800">{t.team_name}</p>
                        {isCritical && (
                          <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full font-semibold">
                            Critical Path {onCritPath}%
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        P50: <strong className="text-blue-600">{t.p50_completion} {unit}</strong>
                        {' '}· P90: <strong className="text-red-500">{t.p90_completion} {unit}</strong>
                      </span>
                    </div>
                    {/* Timeline bar */}
                    <div className="relative h-5 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="absolute left-0 h-full rounded"
                        style={{
                          width: `${(t.p50_completion / (results.primary_metric.p90 * 1.1)) * 100}%`,
                          backgroundColor: isCritical ? '#fca5a5' : '#93c5fd',
                        }}
                      />
                      <div
                        className="absolute left-0 h-full rounded opacity-40"
                        style={{
                          width: `${(t.p90_completion / (results.primary_metric.p90 * 1.1)) * 100}%`,
                          backgroundColor: isCritical ? '#ef4444' : '#3b82f6',
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className="text-xs text-gray-400">Start</span>
                      <span className="text-xs text-gray-400">P90 deadline</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      In <strong>{onCritPath}%</strong> of simulated outcomes, {t.team_name} was the program's
                      rate-limiting constraint.
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── AI Impact ─────────────────────────────────────────────── */}
        {activeTab === 'ai-impact' && hasAI && results.ai_impact && (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">AI Impact Analysis</p>
              <p className="text-xs text-gray-400">Asymmetric compression applied: optimistic 100%, most-likely 85%, pessimistic 40% of stated acceleration rate.</p>
            </div>

            {/* Side by side comparison */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Base Scenario', data: pm, color: '#3b82f6', bg: 'bg-blue-50' },
                { label: 'AI Impact Scenario', data: results.ai_impact.primary_metric, color: '#7c3aed', bg: 'bg-violet-50' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                  <p className="text-xs font-semibold text-gray-600 mb-3">{s.label}</p>
                  <div className="space-y-2">
                    {[['P50', s.data.p50], ['P90', s.data.p90], ['Mean', s.data.mean]].map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{k}</span>
                        <span className="text-sm font-bold" style={{ color: s.color }}>{v} {unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary callout */}
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-violet-800 mb-1">AI Adoption Summary</p>
              <p className="text-sm text-violet-700">
                AI adoption at current assessed readiness moves P90 from{' '}
                <strong>{pm.p90} {unit}</strong> to{' '}
                <strong>{results.ai_impact.primary_metric.p90} {unit}</strong> —
                a <strong>{(pm.p90 - results.ai_impact.primary_metric.p90).toFixed(1)} {unit}</strong> improvement.
                P50 moves from <strong>{pm.p50}</strong> to{' '}
                <strong>{results.ai_impact.primary_metric.p50} {unit}</strong>.
              </p>
              {results.ai_impact.downside_triggered_pct != null && (
                <p className="text-xs text-violet-600 mt-2">
                  AI rework events triggered in <strong>{Math.round(results.ai_impact.downside_triggered_pct * 100)}%</strong> of
                  simulated outcomes.
                </p>
              )}
            </div>

            {/* Overlay histogram */}
            {results.ai_impact.histogram && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Distribution Comparison</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={results.histogram.map((b, i) => ({
                    bin: b.bin,
                    base: b.count,
                    ai: results.ai_impact!.histogram[i]?.count || 0,
                  }))} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                    <XAxis dataKey="bin" tick={{ fontSize: 9, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} />
                    <RechartsTip contentStyle={{ fontSize: 10, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="base" name="Base" fill="#93c5fd" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="ai" name="AI Impact" fill="#8b5cf6" fillOpacity={0.7} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Readiness sensitivity sub-chart */}
            {hasReadiness && results.readiness_sensitivity && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Readiness Sensitivity — What tier do we need to hit the deadline?
                </p>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={results.readiness_sensitivity} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="tier_label" tick={{ fontSize: 10, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                    <RechartsTip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="p50" name="P50" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="p90" name="P90" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Program P50 and P90 at each AI readiness tier — from No Adoption to Advanced
                </p>
              </div>
            )}
          </div>
        )}

        {/* ── Workload ─────────────────────────────────────────────────── */}
        {activeTab === 'workload' && hasWorkload && workload && (
          <div className="p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Per-Person Workload</p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                workload.pass ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
              }`}>
                {workload.pass ? '✓ Within limits' : `✗ ${workload.overloaded_team_count} team(s) over cap`}
              </span>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              The {workload.ceiling_hours} hr/week ceiling is a hard cap per individual. A team's allocation %
              is the per-person commitment — 100% ≈ {workload.standard_week_hours} hrs, a 1-day/week specialist ≈ 8 hrs.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Team</th>
                    <th className="py-2 pr-3 font-medium text-right">People</th>
                    <th className="py-2 pr-3 font-medium text-right">Allocation</th>
                    <th className="py-2 pr-3 font-medium text-right">Hrs / person / wk</th>
                    <th className="py-2 pr-3 font-medium text-right">Team weekly total</th>
                    <th className="py-2 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(workload.per_team || []).map((t, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 pr-3 text-gray-800">{t.team_name}</td>
                      <td className="py-2 pr-3 text-right text-gray-700">{t.size}</td>
                      <td className="py-2 pr-3 text-right text-gray-700">
                        {t.allocation_pct}%
                        {t.allocation_assumed && <span className="text-gray-400 ml-1">(assumed)</span>}
                      </td>
                      <td className={`py-2 pr-3 text-right font-medium ${t.pass ? 'text-gray-700' : 'text-red-600'}`}>
                        {t.hours_per_person} hrs
                      </td>
                      <td className="py-2 pr-3 text-right text-gray-600">{t.team_weekly_total} hrs</td>
                      <td className="py-2 text-right">
                        {t.pass ? (
                          <span className="text-emerald-600">✓</span>
                        ) : (
                          <span className="text-red-600 font-semibold">
                            ✗ {(t.hours_per_person - (workload.ceiling_hours || 45)).toFixed(1)} hrs over
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 font-semibold">
                    <td className="py-2 pr-3 text-gray-700" colSpan={4}>All teams — combined weekly hours</td>
                    <td className="py-2 pr-3 text-right text-gray-800">{workload.team_weekly_total} hrs</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className={`mt-4 rounded-lg p-4 border ${
              workload.pass ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'
            }`}>
              <p className={`text-sm font-semibold mb-1 ${workload.pass ? 'text-emerald-800' : 'text-red-800'}`}>
                {workload.pass ? 'Workload is sustainable' : 'Workload risk'}
              </p>
              <p className={`text-sm ${workload.pass ? 'text-emerald-700' : 'text-red-700'}`}>
                {workload.pass
                  ? `Every team is within the ${workload.ceiling_hours} hr/week per-person ceiling. The plan is staffed at a sustainable intensity.`
                  : `${workload.overloaded_team_count} team(s) require more than ${workload.ceiling_hours} hrs/week per person. Reduce scope, add headcount, or extend the timeline before committing to this plan.`}
              </p>
            </div>
            {(workload.per_team || []).some(t => t.allocation_assumed) && (
              <p className="text-[11px] text-gray-400 mt-2 italic">
                Some teams had no allocation captured in the interview — assumed 100% (full-time). Add allocation in the intake for a precise check.
              </p>
            )}
          </div>
        )}

        {/* ── Scenario Comparison ──────────────────────────────────────── */}
        {activeTab === 'scenarios' && (
          <div className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Scenario Comparison</p>
            <p className="text-xs text-gray-400 mb-4">
              See how the timeline shifts under different decisions. Each scenario re-runs the full Monte Carlo simulation with the levers applied.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {PRESET_SCENARIOS.map(p => (
                <button
                  key={p.name}
                  onClick={() => runScenarioBatch([p])}
                  disabled={scenarioLoading || !manifest}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {p.name}
                </button>
              ))}
              <button
                onClick={() => runScenarioBatch(PRESET_SCENARIOS)}
                disabled={scenarioLoading || !manifest}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {scenarioLoading ? 'Running…' : 'Compare All Presets'}
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 mb-3">Build a custom scenario</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {([
                  { key: 'add_team_members', label: 'Add team members', max: 10, step: 1, suffix: '' },
                  { key: 'reduce_scope_pct', label: 'Reduce scope', max: 60, step: 5, suffix: '%' },
                  { key: 'reduce_variability_pct', label: 'Reduce variability', max: 80, step: 5, suffix: '%' },
                  { key: 'better_training_pct', label: 'Better training / onboarding', max: 30, step: 5, suffix: '%' },
                  { key: 'reduce_sme_deps_pct', label: 'Fewer SME dependencies', max: 50, step: 5, suffix: '%' },
                ] as { key: keyof ScenarioLevers; label: string; max: number; step: number; suffix: string }[]).map(l => (
                  <label key={l.key} className="block">
                    <span className="text-[11px] font-medium text-gray-600 flex justify-between">
                      <span>{l.label}</span>
                      <span className="text-gray-800 font-semibold">{customLevers[l.key] || 0}{l.suffix}</span>
                    </span>
                    <input
                      type="range"
                      min={0}
                      max={l.max}
                      step={l.step}
                      value={customLevers[l.key] || 0}
                      onChange={e => setCustomLevers({ ...customLevers, [l.key]: parseInt(e.target.value, 10) })}
                      className="w-full mt-1 accent-red-600"
                    />
                  </label>
                ))}
              </div>
              <button
                onClick={() => runScenarioBatch([{ name: 'Custom Scenario', levers: customLevers }])}
                disabled={scenarioLoading || !manifest || !hasCustomLevers}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-40"
              >
                Run Custom Scenario
              </button>
            </div>

            {scenarioLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Running Monte Carlo for each scenario…
              </div>
            )}

            {!scenarioLoading && scenarioRows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3 font-medium">Scenario</th>
                      <th className="py-2 pr-3 font-medium text-right">P50 ({unit})</th>
                      {showDates && <th className="py-2 pr-3 font-medium text-right">P50 Date</th>}
                      <th className="py-2 pr-3 font-medium text-right">P90 ({unit})</th>
                      {showDates && <th className="py-2 pr-3 font-medium text-right">P90 Date</th>}
                      <th className="py-2 font-medium text-right">vs Baseline (P50)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarioRows.map((row, i) => {
                      const delta = row.delta_p50 ?? 0;
                      return (
                        <tr key={i} className={`border-b border-gray-100 ${row.is_baseline ? 'bg-gray-50 font-semibold' : ''}`}>
                          <td className="py-2 pr-3 text-gray-800">{row.name}</td>
                          <td className="py-2 pr-3 text-right text-gray-700">{row.p50}</td>
                          {showDates && <td className="py-2 pr-3 text-right text-gray-600">{dateFor(row.p50)}</td>}
                          <td className="py-2 pr-3 text-right text-gray-700">{row.p90}</td>
                          {showDates && <td className="py-2 pr-3 text-right text-gray-600">{dateFor(row.p90)}</td>}
                          <td className="py-2 text-right">
                            {row.is_baseline ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <span className={delta < 0 ? 'text-emerald-600 font-semibold' : delta > 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                                {delta < 0 ? '' : '+'}{delta} {unit}
                                {delta < 0 ? ' faster' : delta > 0 ? ' slower' : ''}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[11px] text-gray-400 mt-3 italic">
                  Headcount scenarios apply Brooks' Law diminishing returns — adding people speeds delivery less per extra head.
                </p>
              </div>
            )}

            {!scenarioLoading && scenarioRows.length === 0 && (
              <div className="text-center py-8 text-sm text-gray-400">
                Pick a preset or build a custom scenario above to compare timelines.
              </div>
            )}
          </div>
        )}

        {/* ── Action Plan ────────────────────────────────────────────── */}
        {activeTab === 'action-plan' && (
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">AI Action Plan</p>
                <p className="text-xs text-gray-400 mt-0.5">Synthesized from sensitivity rankings and simulation results</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={actionTone}
                  onChange={e => setActionTone(e.target.value as any)}
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-red-400"
                >
                  <option value="program_manager">Program Manager</option>
                  <option value="executive">Executive Summary</option>
                  <option value="technical">Technical Deep Dive</option>
                </select>
                <button
                  onClick={generateActionPlan}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {actionLoading ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                    : actionPlan ? <><RefreshCw className="w-3 h-3" /> Regenerate</>
                    : <><ChevronRight className="w-3 h-3" /> Generate</>}
                </button>
              </div>
            </div>
            {actionPlan ? (
              <div className="prose prose-sm max-w-none text-gray-800 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_strong]:font-semibold [&_ul]:space-y-1">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{actionPlan}</ReactMarkdown>
              </div>
            ) : !actionLoading && (
              <div className="text-center py-10">
                <FileText className="w-8 h-8 mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-400">Click Generate to produce a structured action plan</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reset */}
      <button onClick={onReset} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
        <RefreshCw className="w-4 h-4" />
        Start a new simulation
      </button>
    </div>
  );
}
